import type { FunctionComponent } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useToken } from '../../../Flow/ConsentContextProvider';
import { useRovoVisualRefresh } from '../useRovoVisualRefresh';
import Label from '../Label';

const messages = defineMessages({
  header: {
    id: 'oauth.rovo.permissions.header',
    defaultMessage:
      '{appName} will be able to read your data and act on your behalf in Atlassian products.',
    description: 'Header describing permissions',
  },
  permissionsAllowed: {
    id: 'oauth.rovo.permissions.allowed',
    defaultMessage: 'Permissions allowed:',
    description: 'Label shown before the list of permissions',
  },
  permissionsHeading: {
    id: 'oauth.rovo.permissions.heading',
    defaultMessage: 'Permissions',
    description: 'Heading shown above the refreshed permissions card',
  },
});

const LegacyBullets: FunctionComponent = () => {
  const { scopes, clientName } = useToken();
  return (
    <Box>
      <FormattedMessage
        {...messages.header}
        values={{
          appName: <strong>{clientName}</strong>,
        }}
      />
      <FormattedMessage {...messages.permissionsAllowed} tagName="p" />
      <ul>
        {scopes.map((scope) => (
          <li key={scope}>
            <code>{scope}</code>
          </li>
        ))}
      </ul>
    </Box>
  );
};

const cardStyle = xcss({
  borderRadius: 'radius.xxlarge',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  paddingBlock: 'space.200',
  paddingInline: 'space.100',
});

const PermissionsCard: FunctionComponent = () => {
  const { clientName } = useToken();
  return (
    <Stack space="space.050">
      <Label>
        <FormattedMessage {...messages.permissionsHeading} />
      </Label>
      <Box xcss={cardStyle}>
        <Inline space="space.100" alignBlock="start">
          <Box paddingInline="space.050">
            <CheckCircleIcon
              label=""
              size="medium"
              color={token('color.text')}
            />
          </Box>
          <Text as="p">
            <FormattedMessage
              {...messages.header}
              values={{
                appName: <strong>{clientName}</strong>,
              }}
            />
          </Text>
        </Inline>
      </Box>
    </Stack>
  );
};

const PermissionsBullets: FunctionComponent = () => {
  const refreshed = useRovoVisualRefresh();
  return refreshed ? <PermissionsCard /> : <LegacyBullets />;
};

export default PermissionsBullets;
